import type { ChangeEvent, ReactNode } from "react";

export function Section({ title, children, actions }: { title: string; children: ReactNode; actions?: ReactNode }): React.JSX.Element {
  return (
    <section className="inspector-section">
      <div className="section-heading"><span>{title}</span>{actions}</div>
      <div className="section-content">{children}</div>
    </section>
  );
}

export function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: string }): React.JSX.Element {
  return (
    <label className="field-row">
      <span className="field-label">{label}</span>
      <span className="field-control">{children}</span>
      {hint && <span className="field-hint">{hint}</span>}
    </label>
  );
}

export function ExpressionInput({ value, onChange, ariaLabel }: { value: string; onChange: (value: string) => void; ariaLabel?: string }): React.JSX.Element {
  return <input className="expression-input" value={value} aria-label={ariaLabel} spellCheck={false} onChange={(event) => onChange(event.target.value)} />;
}

export function TextInput({ value, onChange, ariaLabel, placeholder }: { value: string; onChange: (value: string) => void; ariaLabel?: string; placeholder?: string }): React.JSX.Element {
  return <input value={value} aria-label={ariaLabel} placeholder={placeholder} onChange={(event) => onChange(event.target.value)} />;
}

export function NumberInput({ value, onChange, min, max, step = 1 }: { value?: number; onChange: (value: number | undefined) => void; min?: number; max?: number; step?: number }): React.JSX.Element {
  return (
    <input
      type="number"
      value={value ?? ""}
      min={min}
      max={max}
      step={step}
      onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value === "" ? undefined : Number(event.target.value))}
    />
  );
}

export function Toggle({ checked, onChange, label }: { checked: boolean; onChange: (checked: boolean) => void; label: string }): React.JSX.Element {
  return (
    <button type="button" role="switch" aria-checked={checked} className={`toggle ${checked ? "is-on" : ""}`} onClick={() => onChange(!checked)}>
      <span className="toggle-thumb" />
      <span className="sr-only">{label}</span>
    </button>
  );
}

export function VectorFields({
  value,
  onChange,
  labels = ["X", "Y", "Z"]
}: {
  value: [string, string, string];
  onChange: (value: [string, string, string]) => void;
  labels?: [string, string, string];
}): React.JSX.Element {
  return (
    <div className="vector-fields">
      {value.map((entry, index) => (
        <label key={labels[index]}>
          <span>{labels[index]}</span>
          <ExpressionInput
            value={entry}
            ariaLabel={labels[index]}
            onChange={(next) => {
              const clone = [...value] as [string, string, string];
              clone[index] = next;
              onChange(clone);
            }}
          />
        </label>
      ))}
    </div>
  );
}
